import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { I18n } from 'aws-amplify';
import { ACCOUNT_USER_TYPE } from '../../../configures/val';
import Input from '../../common/Input';
import Radio from '../../common/Radio';
import Button from '../../common/Button';

import styles from './styles.module.css';

const initialMailAddress = '';
const initialUserType = ACCOUNT_USER_TYPE.GENERAL;

/**
 * ユーザ追加ダイアログコンポーネント
 * @param {Object} props
 * @param {boolean} props.open - ダイアログの表示状態
 * @param {Function} props.onClose - ダイアログを閉じる際に実行する処理
 * @param {Function} props.addUser - ユーザ追加処理
 * @returns {JSX.Element}
 */
const AddUserDialog = ({ open, onClose, addUser }) => {
  const ref = useRef();

  const [mailAddress, setMailAddress] = useState(initialMailAddress);
  const [userType, setUserType] = useState(initialUserType);

  const isValidMailAddress = useMemo(() => {
    const pattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return pattern.test(mailAddress.toLowerCase());
  }, [mailAddress]);

  const handleClose = useCallback(() => {
    onClose();
    setMailAddress(initialMailAddress);
    setUserType(initialUserType);
  }, [onClose, setMailAddress, setUserType]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (ref.current === event.target) {
        handleClose();
      }
    };

    if (open) {
      ref.current.showModal();
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      ref.current.close();
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open, handleClose]);

  return createPortal(
    <dialog ref={ref} className={styles.dialog}>
      <button className={styles.closeButton} onClick={handleClose}>
        &times;
      </button>
      <h2 className={styles.title}>{I18n.get('Add User')}</h2>
      <h3 className={styles.subtitle}>
        {I18n.get('Add a user who has already obtained a Laplace ID to the account.')}
      </h3>
      <div className={styles.content}>
        {
          <>
            <div className={styles.mailAddressInputArea}>
              <p>{I18n.get('Email Address')}</p>
              <Input
                type='email'
                value={mailAddress}
                onChange={(e) => {
                  setMailAddress(e.target.value);
                }}
              />
              {!isValidMailAddress && (
                <div>
                  <small className={styles.invalidMailAddress}>
                    {I18n.get(
                      'Email address is not in the correct format or has not been entered.'
                    )}
                  </small>
                </div>
              )}
            </div>
            <div className={styles.userTypeSelectArea}>
              <p>{I18n.get('Role')}</p>
              <div>
                <Radio
                  value={ACCOUNT_USER_TYPE.ADMIN}
                  checked={userType === ACCOUNT_USER_TYPE.ADMIN}
                  onChange={(e) => setUserType(e.target.value)}
                  label={I18n.get('Admin')}
                />
                <Radio
                  value={ACCOUNT_USER_TYPE.GENERAL}
                  checked={userType === ACCOUNT_USER_TYPE.GENERAL}
                  onChange={(e) => setUserType(e.target.value)}
                  label={I18n.get('General')}
                />
              </div>
              <small>
                {I18n.get(
                  userType === ACCOUNT_USER_TYPE.ADMIN
                    ? 'Administrators can add users, change permissions, etc.'
                    : 'General users can use the license key assigned to the administrator.'
                )}
              </small>
            </div>
          </>
        }
      </div>
      <div className={styles.actions}>
        <Button color='blueGray' text={I18n.get('Cancel')} onClick={handleClose} size='small' />
        <Button
          text={I18n.get('Add')}
          onClick={() => {
            addUser(mailAddress, userType);
            handleClose();
          }}
          disabled={!isValidMailAddress}
          size='small'
        />
      </div>
    </dialog>,
    document.body
  );
};

export default AddUserDialog;
